import { defineNuxtPlugin, markStaleBootReload, recordBootBuildCheck, useCoreConfig, useRuntimeConfig } from '#imports'
import { claimReloadFor, fetchLatestBuildId } from '../utils/latestBuild'

const RELOAD_GRACE_MS = 5000

// Back/forward, tab restore and session restore can take the document from the browser cache without
// asking the server, so the app boots whatever build it was saved with.
const isServedFromCache = (): boolean => {
    const navigation = performance.getEntriesByType?.('navigation')?.[0] as PerformanceNavigationTiming | undefined
    if (!navigation) return false

    return navigation.type === 'back_forward' || navigation.transferSize === 0
}

// A document taken from the browser cache is checked against the deployed build before anything
// else runs, and reloaded once when it is outdated. Nothing has started yet, so nothing is lost.
export default defineNuxtPlugin({
    name: 'stale-boot-reload',
    enforce: 'pre',
    async setup() {
        if (import.meta.dev || useCoreConfig().buildReload?.enabled === false || !isServedFromCache()) return

        const { app, public: { apm } } = useRuntimeConfig()
        const startedAt = performance.now()
        const latest = await fetchLatestBuildId(app)
        const ms = Math.round(performance.now() - startedAt)

        if (!latest) return recordBootBuildCheck(ms, 'unreadable')
        if (latest === app.buildId) return recordBootBuildCheck(ms, 'fresh')
        if (!claimReloadFor(latest)) return recordBootBuildCheck(ms, 'reload_skipped')

        markStaleBootReload({ path: window.location.pathname, version: String(apm?.serviceVersion ?? '') })
        window.location.reload()
        // Hold the outdated app back while the reload is under way, but never for good.
        await new Promise(resolve => setTimeout(resolve, RELOAD_GRACE_MS))
    },
})
